import { h } from '@omni-torn/ui';
import { TERMS_AGREE_NOTE, TERMS_ASK_NOTE, TERMS_ASK_TEXT, TERMS_ASK_TITLE, termsAsk, TERMS_CHANGED_LINK, TERMS_CHANGED_TEXT, TERMS_VERSION } from '@omni-torn/shared';
import { session, signOut } from './session';
import { box } from './ui';
import { savePrefs } from './pages/shared';
import { sharingBox } from './pages/faction';
import { termsTable } from './pages/keys';

/**
 * Omni-Torn's terms on the website (owner's call 2026-11-01: "just make it a banner that says the terms changed and by
 * continuing use you agree to the terms listed here and link to website").
 *
 * - Agreed to an earlier version (1 … N-1): one line above every page — two at most on a 320 px phone — linking to /terms,
 *   with a ✕. Continuing to use Omni-Torn is agreeing, recorded on the server (`terms`): the ✕ (a real click), or using
 *   the next page after the banner was seen. "Seen" = the player's first real click, touch or key press on the page that
 *   showed it (this browser keeps { v, from } per player: the version shown and the server's record then); "using the next
 *   page" = their first real click, touch or key press on a later page. If the server's record went down since ("Delete my
 *   data", on any device), the kept flag is dropped: nothing is agreed for them without the banner (or the table) first.
 * - Never agreed to any version (0: a code sign-in that didn't agree, or "Delete my data" since): not the banner —
 *   continuing isn't consent there — but Torn's table short with Agree / Sign out instead of every page (`termsGate`).
 */

interface Shown {
  /** The terms version the banner showed. */
  v: number;
  /** The server's record for this player when it showed. */
  from: number;
}

const shownKey = (): string => `omni-torn.termsShown.${session.get()?.playerId ?? 0}`;

function readShown(): Shown | null {
  try {
    const o = JSON.parse(localStorage.getItem(shownKey()) ?? 'null') as Partial<Shown> | null;
    return o && typeof o.v === 'number' && typeof o.from === 'number' ? { v: o.v, from: o.from } : null;
  } catch {
    return null;
  }
}

function writeShown(s: Shown): void {
  try {
    localStorage.setItem(shownKey(), JSON.stringify(s));
  } catch {
    /* this visit only: shown again next time */
  }
}

/** Forget that this browser showed the banner (agreed, "Delete my data", or the server's record went down). */
export function forgetTermsShown(): void {
  try {
    localStorage.removeItem(shownKey());
  } catch {
    /* nothing kept */
  }
}

/** The page view the banner was seen on (the same page drawn again isn't "the next page"). */
let shownAt: string | null = null;
/** The current page view's wait for a real click, touch or key press. */
let waiting: (() => void) | null = null;

/** Runs `fn` at the player's first real (trusted) click, touch or key press on this page while it's on screen. */
function onUse(fn: () => void): void {
  waiting?.();
  const types = ['pointerdown', 'touchstart', 'keydown'] as const;
  const on = (e: Event) => {
    if (!e.isTrusted || document.visibilityState !== 'visible') return;
    stop();
    fn();
  };
  const stop = () => {
    for (const t of types) window.removeEventListener(t, on, { capture: true });
    if (waiting === stop) waiting = null;
  };
  for (const t of types) window.addEventListener(t, on, { capture: true, passive: true });
  waiting = stop;
}

async function record(): Promise<void> {
  await savePrefs({ terms: TERMS_VERSION });
  forgetTermsShown();
  document.getElementById('terms-banner')?.remove();
  // Account and Faction show what you share now.
  document.querySelector('[data-card=sharing]')?.replaceWith(sharingBox());
}

/** Never agreed to any version: the table with Agree / Sign out takes the page's place (true: drawn, nothing else runs). */
export function termsGate(view: HTMLElement, agreed: () => void): boolean {
  const m = session.me();
  if (!m || termsAsk(m.prefs.terms) !== 'table') return false;
  waiting?.();
  forgetTermsShown();
  document.getElementById('terms-banner')?.remove();
  const status = h('span', { class: 'save-status', role: 'status' });
  const agree = h('button', { type: 'button', class: 'btn-primary terms-agree' }, 'Agree') as HTMLButtonElement;
  agree.addEventListener('click', async (e) => {
    if (!e.isTrusted) return;
    agree.disabled = true;
    status.className = 'save-status';
    status.textContent = 'Saving…';
    try {
      await savePrefs({ terms: TERMS_VERSION });
      agreed();
    } catch (err) {
      status.className = 'save-status bad';
      status.textContent = (err as Error).message;
      agree.disabled = false;
    }
  });
  const out = h('button', { type: 'button', class: 'btn-small terms-out' }, 'Sign out');
  out.addEventListener('click', () => signOut());
  document.title = `${TERMS_ASK_TITLE} · Omni-Torn`;
  view.replaceChildren(
    box(
      TERMS_ASK_TITLE,
      [h('p', { class: 'terms-new' }, TERMS_ASK_TEXT), termsTable('Read the full terms (every detail and every earlier version)'), h('p', { class: 'muted' }, TERMS_ASK_NOTE), h('div', { class: 'ask-buttons' }, agree, out, status)],
      { id: 'terms-ask' },
    ),
  );
  return true;
}

/** The "terms changed" banner for this page view — or, when an earlier page showed it, the agreement once this one is used. */
export function termsBanner(): void {
  waiting?.();
  const old = document.getElementById('terms-banner');
  const m = session.me();
  if (!m || termsAsk(m.prefs.terms) !== 'banner') {
    old?.remove();
    return;
  }
  const server = m.prefs.terms ?? 0;
  let shown = readShown();
  // The server's record went down since the banner showed ("Delete my data" anywhere): it counts for nothing now.
  if (shown && (shown.from > server || shown.v < TERMS_VERSION)) {
    forgetTermsShown();
    shown = null;
  }
  const draw = () => {
    if (!document.getElementById('terms-banner')) {
      const close = h('button', { type: 'button', class: 'terms-banner-x', 'aria-label': 'Close (you agree to the terms)', title: TERMS_AGREE_NOTE }, '✕') as HTMLButtonElement;
      close.addEventListener('click', (e) => {
        if (!e.isTrusted) return;
        close.disabled = true;
        void record().catch((err: Error) => {
          close.disabled = false;
          close.title = err.message;
        });
      });
      const banner = h('div', { id: 'terms-banner', class: 'terms-banner', role: 'note', 'data-notice': 'terms' }, close, TERMS_CHANGED_TEXT, h('a', { href: '/terms', target: '_blank', rel: 'noopener' }, TERMS_CHANGED_LINK));
      (document.querySelector('.content') ?? document.body).prepend(banner);
    }
    shownAt = location.pathname;
    if (!readShown()) onUse(() => writeShown({ v: TERMS_VERSION, from: server }));
  };
  if (shown && shownAt !== location.pathname) {
    // Seen on an earlier page: using this one is continuing to use Omni-Torn. (Not saved — the server away: shown again.)
    old?.remove();
    onUse(() => void record().catch(draw));
    return;
  }
  draw();
}
